import React, { useRef, useEffect } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import type { CSSProperties } from 'react';
import EditableTitleSkeleton from 'sharedComponents/Skeleton/EditableTitleSkeleton';
import { Box, BoxProps } from '@mui/material';

interface PluginConnectionEditableFieldProps {
  value: string | undefined | null;
  onTitleSave: (newValue: string) => void;
  sx?: TextFieldProps['sx'];
  inputStyle?: CSSProperties;
  isParentLoading?: boolean;
  fullWidth?: boolean;
  containerStyle?: BoxProps['sx'];
  isEditing?: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
}

const PluginConnectionEditableField = ({
  value = '',
  onTitleSave,
  sx = {},
  inputStyle = {},
  isParentLoading = false,
  fullWidth = false,
  containerStyle = {},
  isEditing,
  setIsEditing,
}: PluginConnectionEditableFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = React.useState<string | null>(null);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleCancelClick = () => {
    setInputValue(value);
    setIsEditing(false);
  };

  const handleSaveClick = () => {
    if (inputRef.current) {
      onTitleSave(inputRef.current.value);
    }
    setIsEditing(false);
  };

  if (isParentLoading) {
    return <EditableTitleSkeleton />;
  }

  return (
    <Box width='auto' display='flex' alignItems='center' sx={containerStyle}>
      <TextField
        value={inputValue}
        inputRef={inputRef}
        disabled={!isEditing}
        onChange={(e) => setInputValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        placeholder='- -'
        fullWidth={fullWidth}
        size='small'
        sx={{
          '& input': {
            border: 'none',
            width: `${inputValue ? inputValue.length : 10}ch`,
            ...inputStyle,
          },
          '& fieldset': { border: 'none' },
          '& .MuiInputBase-root': { padding: 0 },
          '& :focus': { bgcolor: 'none' },
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: '#000',
          },
          '& .MuiInputAdornment-root': {
            marginLeft: 0,
          },
          fontFamily: 'Mont-SemiBold',
          fontSize: 24,
          backgroundColor: 'inherit',
          ...sx,
        }}
        autoComplete='off'
      />
      <InputAdornment position='end'>
        {isEditing && (
          <>
            <IconButton onClick={handleCancelClick}>
              <ClearIcon sx={{ color: '#000', width: '20px' }} />
            </IconButton>
            <IconButton onClick={handleSaveClick}>
              <CheckIcon sx={{ color: '#000', width: '20px' }} />
            </IconButton>
          </>
        )}
      </InputAdornment>
    </Box>
  );
};

export default PluginConnectionEditableField;
